import React from "react";
import dayjs from "dayjs";
import {
  AutoComplete,
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Drawer,
  Empty,
  Input,
  Row,
  Select,
  Tag,
  Typography,
} from "antd";
import {
  CarOutlined,
  RotateLeftOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { lenderHypothecationOptions } from "../../../constants/lenderHypothecationOptions";
import { usedCarsDbApi } from "../../../api/usedCars";
import { vehiclesApi } from "../../../api/vehicles";
import PermissionField from "../../permissions/PermissionField";

const { Text } = Typography;

const shellStyle = "rounded-xl border border-slate-200/75 bg-white shadow-sm";

const sectionHeaderLabel =
  "text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400";

const labelClass =
  "text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500";

const fieldWrapClass = "insurance-field-wrap";

const controlStyle = {};

const CleanField = ({ label, required, children, extra }) => (
  <PermissionField module="insurance" section="vehicle" label={label}>
  <div className="pb-1 insurance-field-block" data-ins-field="true">
    <div className={labelClass}>
      {label} {required ? <span className="text-[#FF8EAD]">*</span> : null}
    </div>
    {children}
    {extra ? <div className="mt-1">{extra}</div> : null}
  </div>
  </PermissionField>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-2">
    <span className="text-[12px] text-slate-500">{label}</span>
    <span className="text-right text-[12px] font-semibold text-slate-800">
      {value || "—"}
    </span>
  </div>
);

const formatDisplayDate = (value) =>
  value ? dayjs(value).format("DD MMM YYYY") : "—";

const getInitial = (name) => (name || "?").toString().slice(0, 2).toUpperCase();

const TYPE_OF_VEHICLE_OPTIONS = [
  { label: "Two Wheeler", value: "Two Wheeler" },
  { label: "Four Wheeler", value: "Four Wheeler" },
  { label: "Commercial", value: "Commercial" },
];

const normalizeVehicleToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normalizeVehicleMakeToken = (value) => {
  const token = normalizeVehicleToken(value);
  if (!token) return "";
  if (token === "marutisuzuki" || token === "marutisuzukiindia")
    return "maruti";
  if (token === "bmwindia" || token === "bayerischemotorenwerke") return "bmw";
  return token;
};

const FUEL_OPTIONS = [
  { label: "Petrol", value: "Petrol" },
  { label: "Diesel", value: "Diesel" },
  { label: "Petrol + CNG", value: "Petrol + CNG" },
  { label: "CNG", value: "CNG" },
  { label: "EV", value: "EV" },
  { label: "Hybrid", value: "Hybrid" },
  { label: "Other", value: "Other" },
];

const Step2VehicleDetails = ({
  formData,
  setField,
  handleChange,
  showErrors,
  step2Errors,
  isNewCar,
  isExtendedWarranty,
  registrationLookupLoading,
  registrationLookupOptions,
  handleRegistrationSearch,
  applyVehicleToForm,
  includeDiscontinuedVehicles,
  setIncludeDiscontinuedVehicles,
  makeOptions,
  modelOptions,
  variantOptions,
  isGeneratingTempReg,
  vehiclePotentialMatch,
  vehiclePotentialMatches,
  vehicleMatchLoading,
  vehicleMergeLoading,
  onMergeVehicleMatch,
  customerVehicleRows,
  customerVehicleLoading,
  onRefreshVehicleDerivedFields,
  onHydrateVehicleSelectionOptions,
  onSwitchToUsedCar,
}) => {
  const [usedCarDbSearchValue, setUsedCarDbSearchValue] = React.useState("");
  const [usedCarDbLoading, setUsedCarDbLoading] = React.useState(false);
  const [usedCarDbOptions, setUsedCarDbOptions] = React.useState([]);
  const [usedCarYears, setUsedCarYears] = React.useState([]);

  React.useEffect(() => {
    if (isNewCar) return;
    usedCarsDbApi.getUniqueYears().then((res) => {
      if (res?.success && Array.isArray(res.data)) {
        setUsedCarYears(res.data.map((y) => ({ label: String(y), value: String(y) })));
      }
    }).catch(() => {});
  }, [isNewCar]);

  React.useEffect(() => {
    if (formData.vehicleMake && formData.vehicleModel) {
      setUsedCarDbSearchValue(
        [formData.vehicleMake, formData.vehicleModel, formData.vehicleVariant]
          .filter(Boolean)
          .join(" ")
      );
    } else {
      setUsedCarDbSearchValue("");
    }
  }, [formData.vehicleMake, formData.vehicleModel, formData.vehicleVariant]);

  // "Type & Save" (background, no button): when the user leaves a Make/Model/
  // Variant field with a value that isn't in the current catalogue options,
  // quietly record it as a suggestion term so it shows up in autosuggest for
  // future users too — without ever writing into the scraper-owned vehicles
  // collection directly. Best-effort; failures are silent, same as the Loans
  // module's "+ Add" flow.
  const savedSuggestionTermsRef = React.useRef(new Set());
  const saveVehicleSuggestionTerm = React.useCallback(
    (level, value, options) => {
      if (isNewCar === false) return; // used-car flow has its own master DB search
      const trimmed = String(value || "").trim();
      if (!trimmed) return;
      const alreadyListed = (options || []).some(
        (opt) => String(opt || "").trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (alreadyListed) return;

      const dedupeKey = `${level}:${(formData.vehicleMake || "").toLowerCase()}|${(formData.vehicleModel || "").toLowerCase()}|${trimmed.toLowerCase()}`;
      if (savedSuggestionTermsRef.current.has(dedupeKey)) return;
      savedSuggestionTermsRef.current.add(dedupeKey);

      vehiclesApi
        .addSuggestionTerm({
          level,
          make: formData.vehicleMake,
          model: formData.vehicleModel,
          variant: formData.vehicleVariant,
        })
        .catch(() => {});
    },
    [isNewCar, formData.vehicleMake, formData.vehicleModel, formData.vehicleVariant],
  );

  const usedCarDbSearchDebounceRef = React.useRef(null);

  const onUsedCarDbSearch = React.useCallback((q) => {
    setUsedCarDbSearchValue(q);
    const query = String(q || "").trim();

    if (usedCarDbSearchDebounceRef.current) {
      clearTimeout(usedCarDbSearchDebounceRef.current);
    }

    if (!query || query.length < 2) {
      setUsedCarDbOptions([]);
      return;
    }

    usedCarDbSearchDebounceRef.current = setTimeout(async () => {
      setUsedCarDbLoading(true);
      try {
        const res = await usedCarsDbApi.getUsedCars({ q: query, limit: 20 });
        if (res?.success && Array.isArray(res.data)) {
          const options = res.data.map((car) => ({
            value: `${car.make} ${car.model} ${car.variant} (${car.year})`,
            label: (
              <div style={{ lineHeight: 1.4 }}>
                <div style={{ fontWeight: 700, color: "#1e293b" }}>
                  {car.make} {car.model}{" "}
                  <span style={{ fontWeight: 400, color: "#475569" }}>{car.variant}</span>
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>
                  <span style={{ fontWeight: 600, color: "#2563eb" }}>{car.year}</span>
                  {car.fuel_type ? <span> · {car.fuel_type}</span> : null}
                  {car.cc ? <span> · {car.cc} cc</span> : null}
                  {car.seating_capacity ? <span> · {car.seating_capacity} seats</span> : null}
                  {car.transmission ? <span> · {car.transmission}</span> : null}
                </div>
              </div>
            ),
            carData: car,
          }));
          setUsedCarDbOptions(options);
        } else {
          setUsedCarDbOptions([]);
        }
      } catch (err) {
        console.error("Error searching used cars db:", err);
        setUsedCarDbOptions([]);
      } finally {
        setUsedCarDbLoading(false);
      }
    }, 280);
  }, []);

  const handleSelectUsedCar = React.useCallback(
    (car) => {
      if (!car) return;
      
      setField("vehicleMake", car.make || "");
      setField("vehicleModel", car.model || "");
      setField("vehicleVariant", car.variant || "");
      setField("fuelType", car.fuel_type || "Petrol");
      setField("cubicCapacity", car.cc ? String(car.cc) : "");
      setField("seatingCapacity", car.seating_capacity ? String(car.seating_capacity) : "");
      setField("manufactureYear", car.year ? String(car.year) : "");
      if (!formData.manufactureMonth) {
        setField("manufactureMonth", "01");
      }

      if (car.year && !formData.dateOfReg) {
        setField("dateOfReg", `${car.year}-01-01`);
      }

      setField("typesOfVehicle", "Four Wheeler");

      if (car.ex_showroom_price) {
        setField("exShowroomPrice", car.ex_showroom_price);
      }

      setUsedCarDbSearchValue(`${car.make} ${car.model} ${car.variant} (${car.year})`);

      onRefreshVehicleDerivedFields?.({
        make: car.make,
        model: car.model,
        variant: car.variant,
        preserveExistingOnMiss: true,
      });
    },
    [setField, onRefreshVehicleDerivedFields]
  );

  const registrationPreview = formData.registrationNumber || "Pending";

  const vehicleTitle =
    [formData.vehicleMake, formData.vehicleModel].filter(Boolean).join(" ") ||
    "Vehicle details";

  const vehicleVariant = formData.vehicleVariant || "Variant pending";
  const vehicleInitial = getInitial(formData.vehicleMake || "VH");

  const regDisabled =
    isNewCar && String(formData.registrationAllotted) === "No";
  const shouldShowEwExtra = Boolean(isExtendedWarranty);
  const filteredCustomerVehicleRows = Array.isArray(customerVehicleRows)
    ? customerVehicleRows.filter((row) => {
        const mk = normalizeVehicleMakeToken(row?.make || row?.vehicleMake);
        const md = normalizeVehicleToken(row?.model || row?.vehicleModel);
        const vr = normalizeVehicleToken(row?.variant || row?.vehicleVariant);
        const selectedMake = normalizeVehicleMakeToken(formData.vehicleMake);
        const selectedModel = normalizeVehicleToken(formData.vehicleModel);
        const selectedVariant = normalizeVehicleToken(formData.vehicleVariant);

        if (
          selectedMake &&
          !(mk.includes(selectedMake) || selectedMake.includes(mk))
        ) {
          return false;
        }
        if (
          selectedModel &&
          !(md.includes(selectedModel) || selectedModel.includes(md))
        ) {
          return false;
        }
        if (
          selectedVariant &&
          !(vr.includes(selectedVariant) || selectedVariant.includes(vr))
        )
          return false;
        return true;
      })
    : [];
  const [showAllCustomerVehicles, setShowAllCustomerVehicles] =
    React.useState(false);
  const [isVehicleListDrawerOpen, setIsVehicleListDrawerOpen] =
    React.useState(false);
  const visibleCustomerVehicleRows = showAllCustomerVehicles
    ? filteredCustomerVehicleRows
    : filteredCustomerVehicleRows.slice(0, 4);
  React.useEffect(() => {
    setShowAllCustomerVehicles(false);
  }, [formData.vehicleMake, formData.vehicleModel, formData.vehicleVariant]);

  const hypothecationOptions = [
    { label: "Not applicable", value: "Not applicable" },
    ...lenderHypothecationOptions.map((option) => ({
      label: option.value,
      value: option.value,
    })),
  ];

  const handleUseCustomerVehicle = React.useCallback(
    async (row) => {
      const rawMake = row?.make || row?.vehicleMake;
      const rawModel = row?.model || row?.vehicleModel;
      const rawVariant = row?.variant || row?.vehicleVariant;
      const resolved = await onHydrateVehicleSelectionOptions?.({
        make: rawMake,
        model: rawModel,
        variant: rawVariant,
      });
      const nextMake = resolved?.make || rawMake;
      const nextModel = resolved?.model || rawModel;
      const nextVariant = resolved?.variant || rawVariant;
      applyVehicleToForm({
        ...row,
        make: nextMake,
        model: nextModel,
        variant: nextVariant,
        vehicleMake: nextMake,
        vehicleModel: nextModel,
        vehicleVariant: nextVariant,
      });
      setField("registrationAllotted", "Yes");
      onRefreshVehicleDerivedFields?.({
        make: nextMake,
        model: nextModel,
        variant: nextVariant,
        registrationNumber: row?.registrationNumber || row?.regNo,
        seedRow: row,
        preserveExistingOnMiss: false,
      });
    },
    [
      applyVehicleToForm,
      onHydrateVehicleSelectionOptions,
      onRefreshVehicleDerivedFields,
      setField,
    ],
  );

  const collapseItems = [
    {
      key: "1",
      label: (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-[#DAF3FF] text-slate-700 ring-1 ring-[#9FC0FF]">
            <SearchOutlined />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800">
              Registration & vehicle mapping
            </div>
          </div>
        </div>
      ),
      children: (
        <div className="pt-3">
          <div className="mb-4 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Registration Lookup
            </span>
            <button
              type="button"
              onClick={() => {
                setField("vehicleMake", "");
                setField("vehicleModel", "");
                setField("vehicleVariant", "");
                setField("registrationNumber", "");
                setField("fuelType", "");
                setField("cubicCapacity", "");
                setField("seatingCapacity", "");
                setField("grossVehicleWeight", "");
                setField("engineNumber", "");
                setField("chassisNumber", "");
                setField("registrationAllotted", "Yes");
              }}
              className="group inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] font-bold text-rose-600 transition-all hover:border-rose-300 hover:bg-rose-100"
            >
              <RotateLeftOutlined className="text-[12px] transition-transform group-hover:rotate-[-45deg]" />
              Clear Selection
            </button>
          </div>
          <Row gutter={[16, 16]}>
            {isNewCar ? (
              <Col xs={24} md={8}>
                <div className={fieldWrapClass}>
                  <CleanField label="Registration Allotted?" required>
                    <Select
                      size="large"
                      allowClear
                      value={formData.registrationAllotted || "Yes"}
                      onChange={(nextValue) => {
                        const resolved = nextValue || "Yes";
                        setField("registrationAllotted", resolved);
                        if (
                          resolved === "Yes" &&
                          String(formData.registrationNumber || "")
                            .trim()
                            .toUpperCase()
                            .startsWith("TEMP_REDG_")
                        ) {
                          setField("registrationNumber", "");
                        }
                      }}
                      style={controlStyle}
                      options={[
                        { label: "Yes", value: "Yes" },
                        { label: "No", value: "No" },
                      ]}
                    />
                  </CleanField>
                </div>
              </Col>
            ) : null}

            <Col xs={24} md={isNewCar ? 16 : 24}>
              <div className={fieldWrapClass}>
                <CleanField
                  label="Registration Number"
                  required
                  extra={
                    <span className="text-[11px] text-slate-400">
                      Search and select registration to auto-fill vehicle
                      details.
                    </span>
                  }
                >
                  {regDisabled ? (
                    <Input
                      size="large"
                      allowClear
                      value={formData.registrationNumber}
                      readOnly
                      prefix={<SearchOutlined className="text-slate-400" />}
                      status={
                        showErrors && step2Errors.registrationNumber
                          ? "error"
                          : ""
                      }
                      placeholder="TEMP_REDG_0001"
                    />
                  ) : (
                    <AutoComplete
                      value={formData.registrationNumber}
                      options={registrationLookupOptions}
                      onSearch={handleRegistrationSearch}
                      allowClear
                      size="large"
                      prefix={<SearchOutlined className="text-slate-400" />}
                      status={
                        showErrors && step2Errors.registrationNumber
                          ? "error"
                          : ""
                      }
                      placeholder="e.g. DL01AB1234"
                      onChange={(value) =>
                        setField(
                          "registrationNumber",
                          String(value || "").toUpperCase(),
                        )
                      }
                      onSelect={async (value, option) => {
                        const normalized = String(value || "").toUpperCase();
                        setField("registrationNumber", normalized);
                        if (option?.vehicleData) {
                          const nextMake =
                            option?.vehicleData?.make ||
                            option?.vehicleData?.vehicleMake;
                          const nextModel =
                            option?.vehicleData?.model ||
                            option?.vehicleData?.vehicleModel;
                          const nextVariant =
                            option?.vehicleData?.variant ||
                            option?.vehicleData?.vehicleVariant;
                          const resolved =
                            (await onHydrateVehicleSelectionOptions?.({
                              make: nextMake,
                              model: nextModel,
                              variant: nextVariant,
                            })) || {};
                          const resolvedMake = resolved?.make || nextMake;
                          const resolvedModel = resolved?.model || nextModel;
                          const resolvedVariant =
                            resolved?.variant || nextVariant;
                          applyVehicleToForm({
                            ...option.vehicleData,
                            make: resolvedMake,
                            model: resolvedModel,
                            variant: resolvedVariant,
                            vehicleMake: resolvedMake,
                            vehicleModel: resolvedModel,
                            vehicleVariant: resolvedVariant,
                          });
                          onRefreshVehicleDerivedFields?.({
                            make: resolvedMake,
                            model: resolvedModel,
                            variant: resolvedVariant,
                            registrationNumber:
                              option?.vehicleData?.registrationNumber ||
                              normalized,
                            seedRow: option?.vehicleData,
                            preserveExistingOnMiss: false,
                          });
                        }
                      }}
                      style={{ width: "100%" }}
                      notFoundContent={
                        registrationLookupLoading
                          ? "Searching..."
                          : "No matching registration"
                      }
                    />
                  )}
                </CleanField>
              </div>
              {showErrors && step2Errors.registrationNumber ? (
                <Text type="danger">{step2Errors.registrationNumber}</Text>
              ) : null}
            </Col>

            {shouldShowEwExtra ? (
              <>
                <Col xs={24} md={8}>
                  <div className={fieldWrapClass}>
                    <CleanField label="Reg Authority">
                      <Input
                        size="large"
                        allowClear
                        value={formData.regAuthority}
                        onChange={handleChange("regAuthority")}
                        placeholder="e.g. DL-01"
                      />
                    </CleanField>
                  </div>
                </Col>
              </>
            ) : null}
          </Row>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFE6C6] text-slate-700 ring-1 ring-[#FFE6C6]">
            <CarOutlined />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800">
              Vehicle profile
            </div>
          </div>
        </div>
      ),
      children: (
        <div className="pt-3">
          <Row gutter={[16, 16]}>
            {isNewCar ? (
              <Col xs={24}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Checkbox
                    checked={Boolean(includeDiscontinuedVehicles)}
                    onChange={(e) =>
                      setIncludeDiscontinuedVehicles(Boolean(e?.target?.checked))
                    }
                  >
                    Include discontinued
                  </Checkbox>
                  {onSwitchToUsedCar ? (
                    <button
                      type="button"
                      onClick={onSwitchToUsedCar}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11px] font-bold text-amber-700 transition-all hover:border-amber-400 hover:bg-amber-100"
                    >
                      <CarOutlined className="text-[12px]" />
                      Move to Used Car
                    </button>
                  ) : null}
                </div>
              </Col>
            ) : null}

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Manufacture Month" required>
                  <Input
                    size="large"
                    allowClear
                    value={formData.manufactureMonth}
                    onChange={handleChange("manufactureMonth")}
                    status={
                      showErrors && step2Errors.manufactureMonth ? "error" : ""
                    }
                    placeholder="e.g. 07"
                  />
                </CleanField>
              </div>
              {showErrors && step2Errors.manufactureMonth ? (
                <Text type="danger">{step2Errors.manufactureMonth}</Text>
              ) : null}
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Manufacture Year" required>
                  {!isNewCar && usedCarYears.length > 0 ? (
                    <Select
                      size="large"
                      allowClear
                      showSearch
                      value={formData.manufactureYear || undefined}
                      onChange={(v) => setField("manufactureYear", v || "")}
                      status={showErrors && step2Errors.manufactureYear ? "error" : ""}
                      placeholder="Select year"
                      options={usedCarYears}
                      style={{ width: "100%" }}
                      filterOption={(input, option) =>
                        String(option?.value || "").includes(input)
                      }
                    />
                  ) : (
                    <Input
                      size="large"
                      allowClear
                      value={formData.manufactureYear}
                      onChange={handleChange("manufactureYear")}
                      status={showErrors && step2Errors.manufactureYear ? "error" : ""}
                      placeholder="e.g. 2026"
                    />
                  )}
                </CleanField>
              </div>
              {showErrors && step2Errors.manufactureYear ? (
                <Text type="danger">{step2Errors.manufactureYear}</Text>
              ) : null}
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Date of Reg">
                  <DatePicker
                    size="large"
                    allowClear
                    value={formData.dateOfReg ? dayjs(formData.dateOfReg) : null}
                    onChange={(d) =>
                      setField("dateOfReg", d ? d.format("YYYY-MM-DD") : "")
                    }
                    format={["DD/MM/YYYY", "D/M/YYYY"]}
                    style={{ width: "100%" }}
                    popupClassName="insurance-themed-calendar"
                  />
                </CleanField>
              </div>
            </Col>

            {!isNewCar ? (
              <Col xs={24}>
                <div className={fieldWrapClass}>
                  <CleanField
                    label="Select Used Car (Master DB)"
                    extra={
                      <span className="text-[11px] text-slate-400">
                        Search by Make, Model, Variant, Fuel Type, or Year (e.g. "Ertiga 2023", "Petrol Swift", "ZXI 2022"). Selecting a record auto-fills all vehicle fields.
                      </span>
                    }
                  >
                    <AutoComplete
                      value={usedCarDbSearchValue}
                      options={usedCarDbOptions}
                      onSearch={onUsedCarDbSearch}
                      allowClear
                      size="large"
                      placeholder="e.g. Ertiga 2023, Petrol Swift ZXI"
                      onSelect={(value, option) => {
                        handleSelectUsedCar(option.carData);
                      }}
                      onChange={(value) => {
                        setUsedCarDbSearchValue(value);
                        if (!value) {
                          setUsedCarDbOptions([]);
                        }
                      }}
                      style={{ width: "100%" }}
                      notFoundContent={
                        usedCarDbLoading ? (
                          <span className="text-xs text-slate-500">Searching...</span>
                        ) : usedCarDbSearchValue && usedCarDbSearchValue.length >= 2 ? (
                          <div className="py-2 text-center">
                            <div className="text-xs font-semibold text-slate-500">No matching records found</div>
                            <div className="mt-1 text-[11px] text-slate-400">Try a different keyword, make, model, or year</div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-400">Type at least 2 characters to search</span>
                        )
                      }
                    />
                  </CleanField>
                </div>
              </Col>
            ) : null}

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Vehicle Make" required>
                  <AutoComplete
                    size="large"
                    allowClear
                    value={formData.vehicleMake || ""}
                    placeholder="Select or type Make"
                    onChange={(val) => {
                      setField("vehicleMake", val || "");
                      setField("vehicleModel", "");
                      setField("vehicleVariant", "");
                      setField("fuelType", "");
                      setField("cubicCapacity", "");
                    }}
                    style={controlStyle}
                    options={makeOptions.map((make) => ({ value: make }))}
                    filterOption={(input, option) =>
                      String(option?.value || "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    notFoundContent={
                      formData.vehicleMake ? (
                        <div className="py-2 text-center">
                          <div className="text-xs font-semibold text-slate-500">Not in the catalogue yet</div>
                          <div className="mt-1 text-[11px] text-slate-400">
                            Keep typing — your entry will be used as-is
                          </div>
                        </div>
                      ) : null
                    }
                    onBlur={() => saveVehicleSuggestionTerm("make", formData.vehicleMake, makeOptions)}
                    status={
                      showErrors && step2Errors.vehicleMake ? "error" : ""
                    }
                  />
                </CleanField>
              </div>
              {showErrors && step2Errors.vehicleMake ? (
                <Text type="danger">{step2Errors.vehicleMake}</Text>
              ) : null}
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Vehicle Model" required>
                  <AutoComplete
                    size="large"
                    allowClear
                    value={formData.vehicleModel || ""}
                    placeholder="Select or type Model"
                    onChange={(val) => {
                      setField("vehicleModel", val || "");
                      setField("vehicleVariant", "");
                      setField("fuelType", "");
                      setField("cubicCapacity", "");
                    }}
                    disabled={!formData.vehicleMake}
                    style={controlStyle}
                    options={modelOptions.map((model) => ({ value: model }))}
                    filterOption={(input, option) =>
                      String(option?.value || "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    notFoundContent={
                      formData.vehicleModel ? (
                        <div className="py-2 text-center">
                          <div className="text-xs font-semibold text-slate-500">Not in the catalogue yet</div>
                          <div className="mt-1 text-[11px] text-slate-400">
                            Keep typing — your entry will be used as-is
                          </div>
                        </div>
                      ) : null
                    }
                    onBlur={() => saveVehicleSuggestionTerm("model", formData.vehicleModel, modelOptions)}
                    status={
                      showErrors && step2Errors.vehicleModel ? "error" : ""
                    }
                  />
                </CleanField>
              </div>
              {showErrors && step2Errors.vehicleModel ? (
                <Text type="danger">{step2Errors.vehicleModel}</Text>
              ) : null}
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Vehicle Variant" required>
                  <AutoComplete
                    size="large"
                    allowClear
                    value={formData.vehicleVariant || ""}
                    placeholder="Select or type Variant"
                    onChange={(val) => {
                      setField("vehicleVariant", val || "");
                      setField("fuelType", "");
                      setField("cubicCapacity", "");
                      if (val) {
                        onRefreshVehicleDerivedFields?.({
                          make: formData.vehicleMake,
                          model: formData.vehicleModel,
                          variant: val,
                          registrationNumber: formData.registrationNumber,
                          preserveExistingOnMiss: false,
                        });
                      }
                    }}
                    disabled={!formData.vehicleMake || !formData.vehicleModel}
                    style={controlStyle}
                    options={variantOptions.map((variant) => ({
                      value: variant,
                    }))}
                    filterOption={(input, option) =>
                      String(option?.value || "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    notFoundContent={
                      formData.vehicleVariant ? (
                        <div className="py-2 text-center">
                          <div className="text-xs font-semibold text-slate-500">Not in the catalogue yet</div>
                          <div className="mt-1 text-[11px] text-slate-400">
                            Keep typing — your entry will be used as-is
                          </div>
                        </div>
                      ) : null
                    }
                    onBlur={() => saveVehicleSuggestionTerm("variant", formData.vehicleVariant, variantOptions)}
                    status={
                      showErrors && step2Errors.vehicleVariant ? "error" : ""
                    }
                  />
                </CleanField>
              </div>
              {showErrors && step2Errors.vehicleVariant ? (
                <Text type="danger">{step2Errors.vehicleVariant}</Text>
              ) : null}
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Fuel Type">
                  <Select
                    size="large"
                    allowClear
                    value={formData.fuelType || undefined}
                    onChange={(v) => setField("fuelType", v || "")}
                    style={controlStyle}
                    placeholder="Fuel type"
                    options={FUEL_OPTIONS}
                  />
                </CleanField>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Cubic Capacity (cc)">
                  <Input
                    size="large"
                    allowClear
                    value={formData.cubicCapacity}
                    onChange={handleChange("cubicCapacity")}
                    placeholder="Auto-filled"
                  />
                </CleanField>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Type of Vehicle">
                  <Select
                    size="large"
                    allowClear
                    value={formData.typesOfVehicle || "Four Wheeler"}
                    onChange={(v) =>
                      setField("typesOfVehicle", v || "Four Wheeler")
                    }
                    style={controlStyle}
                    options={TYPE_OF_VEHICLE_OPTIONS}
                  />
                </CleanField>
              </div>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-[#DAF3FF] text-slate-700 ring-1 ring-[#9FC0FF]">
            <SafetyCertificateOutlined />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800">
              Identity & registration
            </div>
          </div>
        </div>
      ),
      children: (
        <div className="pt-3">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Engine Number" required>
                  <Input
                    size="large"
                    allowClear
                    value={formData.engineNumber}
                    onChange={handleChange("engineNumber")}
                    status={
                      showErrors && step2Errors.engineNumber ? "error" : ""
                    }
                    placeholder="Engine #"
                  />
                </CleanField>
              </div>
              {showErrors && step2Errors.engineNumber ? (
                <Text type="danger">{step2Errors.engineNumber}</Text>
              ) : null}
            </Col>

            <Col xs={24} md={8}>
              <div className={fieldWrapClass}>
                <CleanField label="Chassis Number" required>
                  <Input
                    size="large"
                    allowClear
                    value={formData.chassisNumber}
                    onChange={handleChange("chassisNumber")}
                    status={
                      showErrors && step2Errors.chassisNumber ? "error" : ""
                    }
                    placeholder="Chassis #"
                  />
                </CleanField>
              </div>
              {showErrors && step2Errors.chassisNumber ? (
                <Text type="danger">{step2Errors.chassisNumber}</Text>
              ) : null}
            </Col>

            {shouldShowEwExtra ? (
              <Col xs={24} md={16}>
                <div className={fieldWrapClass}>
                  <CleanField label="Hypothecation">
                    <Select
                      size="large"
                      allowClear
                      value={formData.hypothecation || "Not applicable"}
                      onChange={(v) =>
                        setField("hypothecation", v || "Not applicable")
                      }
                      style={controlStyle}
                      options={hypothecationOptions}
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.label || "")
                          .toLowerCase()
                          .includes(String(input || "").toLowerCase())
                      }
                    />
                  </CleanField>
                </div>
              </Col>
            ) : null}

            {shouldShowEwExtra ? (
              <>
                <Col xs={24} md={8}>
                  <div className={fieldWrapClass}>
                    <CleanField label="Battery Number">
                      <Input
                        size="large"
                        allowClear
                        value={formData.batteryNumber}
                        onChange={handleChange("batteryNumber")}
                        placeholder="Battery number"
                      />
                    </CleanField>
                  </div>
                </Col>

                <Col xs={24} md={8}>
                  <div className={fieldWrapClass}>
                    <CleanField label="Charger Number">
                      <Input
                        size="large"
                        allowClear
                        value={formData.chargerNumber}
                        onChange={handleChange("chargerNumber")}
                        placeholder="Charger number"
                      />
                    </CleanField>
                  </div>
                </Col>
              </>
            ) : null}
          </Row>
        </div>
      ),
    },
  ];

  return (
    <div className="step2-vehicle-details step2-revamp insurance-step2 flex flex-col gap-6">
      <style>
        {`
          .step2-revamp {
            --step2-primary: #2563eb;
            --step2-secondary: #7c3aed;
            --step2-accent: #0ea5e9;
            --step2-success: #10b981;
            --step2-warning: #f59e0b;
            --step2-surface: #ffffff;
            --step2-border: #d9e4f7;
          }

        `}
      </style>
      <div className="rounded-xl border border-slate-200/65 bg-gradient-to-r from-sky-50/90 via-white to-amber-50/50 p-4 shadow-sm sm:p-5 md:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className={sectionHeaderLabel}>Vehicle information</div>
            <div className="mt-1 text-xl font-black tracking-tight text-slate-800 sm:text-2xl">
              Vehicle details
            </div>
            <div className="mt-1 max-w-xl text-sm leading-6 text-slate-500 sm:max-w-2xl">
              Registration-first capture with make/model autofill and
              policy-safe identity fields
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Tag
              className="!rounded-full !px-3 !py-1 !text-[11px] !font-bold"
              color="blue"
            >
              Reg: {registrationPreview}
            </Tag>
            <Tag className="ins-pill-blue !rounded-full !px-3 !py-1 !text-[11px] !font-bold">
              {formData.fuelType || "Fuel pending"}
            </Tag>
            <Tag className="ins-pill-amber !rounded-full !px-3 !py-1 !text-[11px] !font-bold">
              {formData.typesOfVehicle || "Four Wheeler"}
            </Tag>
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]} align="stretch">
        {/* Sidebar — show BELOW the form on mobile (order-2), LEFT on desktop (xl:order-1) */}
        <Col xs={24} xl={8} className="xl:self-stretch order-2 xl:order-1">
          <div className="flex flex-col gap-4 xl:sticky xl:top-[150px]">
            <div className="relative overflow-hidden rounded-xl border border-slate-200/70 bg-white shadow-sm">
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#9FC0FF]/70 text-xs font-black text-slate-800 ring-1 ring-[#9FC0FF]">
                      {vehicleInitial}
                    </div>
                    <div className="min-w-0">
                      <p className="m-0 truncate text-sm font-bold leading-tight text-slate-800">
                        {vehicleTitle}
                      </p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] text-slate-500">
                          {vehicleVariant}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <p className="m-0 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      Policy Type
                    </p>
                    <p className="m-0 max-w-[120px] truncate text-sm font-black text-slate-800">
                      {isExtendedWarranty ? "EW Policy" : "Insurance"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mx-5 border-t border-slate-100" />

              <div className="px-5 pt-5 pb-3">
                <p className="m-0 mb-3 text-sm font-black text-slate-800">
                  Vehicle Snapshot
                </p>

                <SummaryRow
                  label="Registration Number"
                  value={formData.registrationNumber}
                />
                <SummaryRow
                  label="Reg Allotted"
                  value={formData.registrationAllotted || "Yes"}
                />
                <SummaryRow label="Make" value={formData.vehicleMake} />
                <SummaryRow label="Model" value={formData.vehicleModel} />
                <SummaryRow label="Variant" value={formData.vehicleVariant} />
                <SummaryRow label="Fuel" value={formData.fuelType} />
                <SummaryRow
                  label="Cubic Capacity"
                  value={formData.cubicCapacity}
                />
                <SummaryRow
                  label="Engine Number"
                  value={formData.engineNumber}
                />
                <SummaryRow
                  label="Chassis Number"
                  value={formData.chassisNumber}
                />
                <SummaryRow label="Type" value={formData.typesOfVehicle} />
                <SummaryRow
                  label="Manufacture"
                  value={
                    formData.manufactureMonth && formData.manufactureYear
                      ? `${formData.manufactureMonth}/${formData.manufactureYear}`
                      : "—"
                  }
                />
                <SummaryRow
                  label="Date of Reg"
                  value={formatDisplayDate(formData.dateOfReg)}
                />
                {shouldShowEwExtra ? (
                  <SummaryRow
                    label="Hypothecation"
                    value={formData.hypothecation || "Not applicable"}
                  />
                ) : null}

                {shouldShowEwExtra ? (
                  <>
                    <SummaryRow
                      label="Reg Authority"
                      value={formData.regAuthority}
                    />
                    <SummaryRow
                      label="Battery Number"
                      value={formData.batteryNumber}
                    />
                    <SummaryRow
                      label="Charger Number"
                      value={formData.chargerNumber}
                    />
                  </>
                ) : null}
              </div>
            </div>

            {!isNewCar &&
            (customerVehicleLoading ||
              filteredCustomerVehicleRows.length > 0) ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_6px_18px_rgba(15,23,42,0.06)]">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                    Customer Vehicles
                  </p>
                  <div className="flex items-center gap-1.5 text-[10px]">
                    {filteredCustomerVehicleRows.length > 0 ? (
                      <Tag className="!m-0 !rounded-full" color="default">
                        {filteredCustomerVehicleRows.length} match
                        {filteredCustomerVehicleRows.length > 1 ? "es" : ""}
                      </Tag>
                    ) : null}
                    {filteredCustomerVehicleRows.length > 0 ? (
                      <button
                        type="button"
                        className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-600 transition hover:border-slate-300"
                        onClick={() => setIsVehicleListDrawerOpen(true)}
                      >
                        See list of all cars
                      </button>
                    ) : null}
                    {filteredCustomerVehicleRows.length > 4 ? (
                      <button
                        type="button"
                        className="rounded-full border border-[#9FC0FF] bg-[#DAF3FF] px-2 py-0.5 font-semibold text-slate-700 transition hover:border-[#BFD8CD]"
                        onClick={() =>
                          setShowAllCustomerVehicles((prev) => !prev)
                        }
                      >
                        {showAllCustomerVehicles ? "Show less" : "View more"}
                      </button>
                    ) : null}
                  </div>
                </div>
                {customerVehicleLoading ? (
                  <p className="m-0 text-xs text-slate-500">
                    Loading vehicles linked to this customer...
                  </p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {visibleCustomerVehicleRows.map((row, idx) => {
                      const rawReg = String(
                        row?.registrationNumber || row?.regNo || "",
                      ).trim();
                      const isTempReg = rawReg
                        .toUpperCase()
                        .startsWith("TEMP_REDG_");
                      const hasNoReg = !rawReg;
                      return (
                        <button
                          key={String(
                            row?._id ||
                              row?.registrationNumber ||
                              row?.regNo ||
                              row?.chassisNumber ||
                              row?.engineNumber ||
                              `vehicle-${idx}`,
                          )}
                          type="button"
                          onClick={() => handleUseCustomerVehicle(row)}
                          className="group w-full rounded-xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 px-3 py-2 text-left transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-1.5">
                                <p className="m-0 text-xs font-semibold text-slate-800">
                                  {hasNoReg || isTempReg
                                    ? "No Registration Number"
                                    : rawReg}
                                </p>
                                {isTempReg ? (
                                  <Tag
                                    className="!m-0 !rounded-full"
                                    color="orange"
                                  >
                                    Temporary
                                  </Tag>
                                ) : hasNoReg ? (
                                  <Tag
                                    className="!m-0 !rounded-full"
                                    color="default"
                                  >
                                    No Reg
                                  </Tag>
                                ) : null}
                              </div>
                              <p className="m-0 text-[11px] text-slate-500">
                                {[row?.make, row?.model, row?.variant]
                                  .filter(Boolean)
                                  .join(" ")}
                              </p>
                              <p className="m-0 text-[10px] text-slate-400">
                                {[row?.customerName, row?.primaryMobile]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            </div>
                            <span className="text-[10px] font-semibold text-slate-700 opacity-80 transition group-hover:opacity-100">
                              Use this
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}

            {(vehicleMatchLoading ||
              vehiclePotentialMatch ||
              (Array.isArray(vehiclePotentialMatches) &&
                vehiclePotentialMatches.length > 0)) && (
              <div className="rounded-2xl border border-amber-200/70 bg-amber-50/70 p-3 sm:p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                    Potential Vehicle Match
                  </p>
                  {vehiclePotentialMatch ? (
                    <Tag className="!m-0 !rounded-full" color="gold">
                      Score {vehiclePotentialMatch.score || 0}
                    </Tag>
                  ) : null}
                </div>

                {vehicleMatchLoading ? (
                  <p className="m-0 text-xs text-slate-500">
                    Scanning vehicle_master_records...
                  </p>
                ) : vehiclePotentialMatch ? (
                  <>
                    <div className="rounded-xl border border-amber-200 bg-white px-3 py-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="m-0 text-sm font-bold text-slate-800">
                            {vehiclePotentialMatch.registrationNumber || "—"}
                          </p>
                          <p className="m-0 text-xs text-slate-500">
                            {[
                              vehiclePotentialMatch.make,
                              vehiclePotentialMatch.model,
                              vehiclePotentialMatch.variant,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                          <p className="m-0 text-[11px] text-slate-400">
                            {vehiclePotentialMatch.customerName ||
                              "Customer unavailable"}
                            {vehiclePotentialMatch.primaryMobile
                              ? ` · ${vehiclePotentialMatch.primaryMobile}`
                              : ""}
                          </p>
                        </div>
                        <Button
                          type="primary"
                          size="middle"
                          loading={vehicleMergeLoading}
                          onClick={() =>
                            onMergeVehicleMatch?.(vehiclePotentialMatch)
                          }
                        >
                          Merge
                        </Button>
                      </div>
                    </div>

                    {Array.isArray(vehiclePotentialMatches) &&
                    vehiclePotentialMatches.length > 1 ? (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {vehiclePotentialMatches.slice(1, 3).map((row, idx) => (
                          <button
                            type="button"
                            key={String(
                              row?._id ||
                                row?.registrationNumber ||
                                row?.regNo ||
                                row?.chassisNumber ||
                                row?.engineNumber ||
                                `match-${idx}`,
                            )}
                            onClick={() => onMergeVehicleMatch?.(row)}
                            className="w-full rounded-xl border border-amber-200/80 bg-white px-3 py-2 text-left transition hover:border-amber-300"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="m-0 text-xs font-semibold text-slate-700">
                                  {row?.registrationNumber ||
                                    row?.regNo ||
                                    "Pending Registration"}
                                </p>
                                <p className="m-0 text-[11px] text-slate-500">
                                  {[row?.make, row?.model, row?.variant]
                                    .filter(Boolean)
                                    .join(" ")}
                                </p>
                              </div>
                              <span className="text-[10px] text-amber-700">
                                Merge this
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="m-0 text-xs text-slate-500">
                    No historical match found yet.
                  </p>
                )}
              </div>
            )}
          </div>
        </Col>

        {/* Form — show FIRST on mobile (order-1), RIGHT on desktop (xl:order-2) */}
        <Col xs={24} xl={16} className="order-1 xl:order-2">
          <div className="flex flex-col gap-4">
            {collapseItems.map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-slate-200/75 bg-white p-4 shadow-sm sm:p-5"
              >
                <div className="pb-3 border-b border-slate-100">
                  {item.label}
                </div>
                {item.children}
              </div>
            ))}
          </div>
        </Col>
      </Row>
      <Drawer
        title="Customer vehicles"
        placement="right"
        width={520}
        open={isVehicleListDrawerOpen}
        onClose={() => setIsVehicleListDrawerOpen(false)}
      >
        {filteredCustomerVehicleRows.length ? (
          <div className="flex flex-col gap-2">
            {filteredCustomerVehicleRows.map((row, idx) => (
              <button
                key={`drawer-${String(
                  row?._id ||
                    row?.registrationNumber ||
                    row?.regNo ||
                    row?.chassisNumber ||
                    row?.engineNumber ||
                    `vehicle-${idx}`,
                )}`}
                type="button"
                onClick={() => {
                  handleUseCustomerVehicle(row);
                  setIsVehicleListDrawerOpen(false);
                }}
                className="group w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-slate-300 hover:shadow-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="m-0 text-xs font-semibold text-slate-800">
                      {row?.registrationNumber ||
                        row?.regNo ||
                        "Pending Registration"}
                    </p>
                    <p className="m-0 text-[11px] text-slate-500">
                      {[row?.make, row?.model, row?.variant]
                        .filter(Boolean)
                        .join(" ")}
                    </p>
                    <p className="m-0 text-[10px] text-slate-400">
                      {[row?.customerName, row?.primaryMobile]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <span className="text-[10px] font-semibold text-slate-700 opacity-80 group-hover:opacity-100">
                    Use this
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <Empty
            description="No customer vehicles available for current make/model filters."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Drawer>
    </div>
  );
};

export default Step2VehicleDetails;
